export const T12FAQ = () => {
  return (
    <section className="py-20 bg-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="inline-block px-4 py-2 bg-teal-50 border border-teal-200 rounded-full mb-6">
            <span className="text-teal-600 text-sm font-semibold">Common Questions</span>
          </div>
          <h2 className="text-4xl font-bold text-gray-900 mb-6">
            Frequently Asked Questions About T12
          </h2>
          <p className="text-xl text-gray-700">
            Get answers to the most common questions apartment building sellers ask about Trailing 12 financial statements.
          </p>
        </div>
        
        <div className="space-y-4">
          <details className="bg-gradient-to-br from-slate-50 to-gray-50 rounded-xl border border-gray-200 overflow-hidden group">
            <summary className="flex items-center justify-between p-6 cursor-pointer">
              <h3 className="text-lg font-bold text-gray-900 pr-4">What does Trailing 12 (T12) mean in real estate?</h3>
              <i className="ri-arrow-down-s-line text-2xl text-teal-600 group-open:rotate-180 transition-transform"></i>
            </summary>
            <div className="px-6 pb-6 text-gray-700">
              <p className="mb-3">
                Trailing 12 (T12) refers to the financial performance of a property over the most recent 12-month period. It provides a comprehensive view of income, expenses, and net operating income (NOI) based on actual historical data rather than projections or pro forma estimates.
              </p>
              <p>
                The "trailing" aspect means the data rolls forward continuously. If today is June 15, 2024, your T12 would cover June 1, 2023 through May 31, 2024. Next month, it would shift to July 1, 2023 through June 30, 2024.
              </p>
            </div>
          </details>
          
          <details className="bg-gradient-to-br from-slate-50 to-gray-50 rounded-xl border border-gray-200 overflow-hidden group">
            <summary className="flex items-center justify-between p-6 cursor-pointer">
              <h3 className="text-lg font-bold text-gray-900 pr-4">Why is T12 important when selling an apartment building?</h3>
              <i className="ri-arrow-down-s-line text-2xl text-teal-600 group-open:rotate-180 transition-transform"></i>
            </summary>
            <div className="px-6 pb-6 text-gray-700">
              <p className="mb-3">
                T12 is critical because it directly impacts property valuation and buyer confidence. Buyers use T12 to calculate NOI and cap rate, which determine the property's market value using the formula: Property Value = T12 NOI ÷ Cap Rate.
              </p>
              <p>
                Strong T12 financials can increase your sale price by 15-25%, while weak or incomplete T12 data can reduce offers or kill deals entirely. It's also required by lenders to underwrite loans for buyers, making it essential for 70-80% of potential buyers who need financing.
              </p>
            </div>
          </details>
          
          <details className="bg-gradient-to-br from-slate-50 to-gray-50 rounded-xl border border-gray-200 overflow-hidden group">
            <summary className="flex items-center justify-between p-6 cursor-pointer">
              <h3 className="text-lg font-bold text-gray-900 pr-4">What's the difference between T12 and TTM?</h3>
              <i className="ri-arrow-down-s-line text-2xl text-teal-600 group-open:rotate-180 transition-transform"></i>
            </summary>
            <div className="px-6 pb-6 text-gray-700">
              <p>
                T12 (Trailing 12) and TTM (Trailing Twelve Months) are essentially the same concept - both refer to the most recent 12-month period of financial performance. The terms are used interchangeably in commercial real estate, though T12 is more common in apartment building transactions. If a buyer asks for TTM, provide your T12 - they're asking for the same document.
              </p>
            </div>
          </details>
          
          <details className="bg-gradient-to-br from-slate-50 to-gray-50 rounded-xl border border-gray-200 overflow-hidden group">
            <summary className="flex items-center justify-between p-6 cursor-pointer">
              <h3 className="text-lg font-bold text-gray-900 pr-4">How do you calculate T12 NOI?</h3>
              <i className="ri-arrow-down-s-line text-2xl text-teal-600 group-open:rotate-180 transition-transform"></i>
            </summary>
            <div className="px-6 pb-6 text-gray-700">
              <p className="mb-3">
                T12 NOI is calculated by taking Total Income (rental income + other income) minus Total Operating Expenses (property taxes, insurance, utilities, maintenance, management fees, etc.) over the trailing 12-month period.
              </p>
              <div className="bg-white rounded-lg p-4 border border-gray-200 mb-3">
                <p className="font-semibold text-gray-900 mb-2">Formula:</p>
                <p className="text-sm">T12 NOI = T12 Gross Income - T12 Operating Expenses</p>
              </div>
              <p className="text-sm">
                <strong>Important:</strong> Debt service (mortgage payments) and capital expenditures (major improvements) are NOT included in NOI calculations.
              </p>
            </div>
          </details>
          
          <details className="bg-gradient-to-br from-slate-50 to-gray-50 rounded-xl border border-gray-200 overflow-hidden group">
            <summary className="flex items-center justify-between p-6 cursor-pointer">
              <h3 className="text-lg font-bold text-gray-900 pr-4">What documents do I need to provide for T12 analysis?</h3>
              <i className="ri-arrow-down-s-line text-2xl text-teal-600 group-open:rotate-180 transition-transform"></i>
            </summary>
            <div className="px-6 pb-6 text-gray-700">
              <p className="mb-3">Essential T12 documents include:</p>
              <ul className="space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-teal-600 mt-0.5"></i>
                  <span>Monthly income statements for the past 12 months</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-teal-600 mt-0.5"></i>
                  <span>Rent roll showing current tenant information and rental rates</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-teal-600 mt-0.5"></i>
                  <span>Operating expense records with receipts</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-teal-600 mt-0.5"></i>
                  <span>Property tax statements and insurance policies</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-teal-600 mt-0.5"></i>
                  <span>Utility bills and maintenance invoices</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-teal-600 mt-0.5"></i>
                  <span>Bank statements showing actual deposits and payments</span>
                </li>
              </ul>
            </div>
          </details>
          
          <details className="bg-gradient-to-br from-slate-50 to-gray-50 rounded-xl border border-gray-200 overflow-hidden group">
            <summary className="flex items-center justify-between p-6 cursor-pointer">
              <h3 className="text-lg font-bold text-gray-900 pr-4">Can I use projected income instead of actual T12?</h3>
              <i className="ri-arrow-down-s-line text-2xl text-teal-600 group-open:rotate-180 transition-transform"></i>
            </summary>
            <div className="px-6 pb-6 text-gray-700">
              <p className="mb-3">
                No. Buyers and lenders require actual T12 data based on historical performance, not projections. While you can provide pro forma projections showing potential income after improvements, these must be clearly separated from actual T12 figures.
              </p>
              <p className="text-sm bg-red-50 border-l-4 border-red-500 rounded-r-lg p-4">
                <strong>Warning:</strong> Mixing projected and actual numbers is a major red flag that can kill your deal. Buyers will verify T12 against bank statements and tax returns, and any discrepancies will destroy trust and reduce offers significantly.
              </p>
            </div>
          </details>
          
          <details className="bg-gradient-to-br from-slate-50 to-gray-50 rounded-xl border border-gray-200 overflow-hidden group">
            <summary className="flex items-center justify-between p-6 cursor-pointer">
              <h3 className="text-lg font-bold text-gray-900 pr-4">What are common T12 red flags that concern buyers?</h3>
              <i className="ri-arrow-down-s-line text-2xl text-teal-600 group-open:rotate-180 transition-transform"></i>
            </summary>
            <div className="px-6 pb-6 text-gray-700">
              <p className="mb-3">Major red flags include:</p>
              <ul className="space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <i className="ri-close-circle-fill text-red-600 mt-0.5"></i>
                  <span>Declining rental income trends over the 12-month period</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-close-circle-fill text-red-600 mt-0.5"></i>
                  <span>Increasing vacancy rates (especially above 10%)</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-close-circle-fill text-red-600 mt-0.5"></i>
                  <span>Unusually low expenses suggesting deferred maintenance</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-close-circle-fill text-red-600 mt-0.5"></i>
                  <span>Missing or incomplete documentation</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-close-circle-fill text-red-600 mt-0.5"></i>
                  <span>Significant one-time income items inflating revenue</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-close-circle-fill text-red-600 mt-0.5"></i>
                  <span>Expenses that don't match industry standards</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-close-circle-fill text-red-600 mt-0.5"></i>
                  <span>Inconsistent month-to-month performance</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-close-circle-fill text-red-600 mt-0.5"></i>
                  <span>Discrepancies between rent roll and actual collected rent</span>
                </li>
              </ul>
            </div>
          </details>
          
          <details className="bg-gradient-to-br from-slate-50 to-gray-50 rounded-xl border border-gray-200 overflow-hidden group">
            <summary className="flex items-center justify-between p-6 cursor-pointer">
              <h3 className="text-lg font-bold text-gray-900 pr-4">How does T12 affect my apartment building's value?</h3>
              <i className="ri-arrow-down-s-line text-2xl text-teal-600 group-open:rotate-180 transition-transform"></i>
            </summary>
            <div className="px-6 pb-6 text-gray-700">
              <p className="mb-3">
                T12 directly determines property value through the income capitalization approach. The formula is: <strong>Property Value = T12 NOI ÷ Cap Rate</strong>.
              </p>
              <div className="bg-white rounded-lg p-4 border border-gray-200 mb-3">
                <p className="font-semibold text-gray-900 mb-2">Example:</p>
                <p className="text-sm mb-2">If your T12 NOI is $500,000 and the market cap rate is 6%, your property value is approximately $8.33 million.</p>
                <p className="text-sm text-teal-600 font-semibold">Every $10,000 increase in T12 NOI can add $166,667 to your property value at a 6% cap rate.</p>
              </div>
            </div>
          </details>
          
          <details className="bg-gradient-to-br from-slate-50 to-gray-50 rounded-xl border border-gray-200 overflow-hidden group">
            <summary className="flex items-center justify-between p-6 cursor-pointer">
              <h3 className="text-lg font-bold text-gray-900 pr-4">Should I wait to sell if my T12 is weak?</h3>
              <i className="ri-arrow-down-s-line text-2xl text-teal-600 group-open:rotate-180 transition-transform"></i>
            </summary>
            <div className="px-6 pb-6 text-gray-700">
              <p className="mb-3">
                It depends on your situation. If you can improve T12 performance within 3-6 months through rent increases, reducing vacancies, or cutting unnecessary expenses, waiting may increase your sale price significantly.
              </p>
              <p className="mb-3">
                However, if improvements will take 12+ months or require substantial capital investment, selling as-is to a cash buyer who understands value-add opportunities may be your best option.
              </p>
              <p className="text-sm bg-teal-50 border-l-4 border-teal-500 rounded-r-lg p-4">
                <strong>Consider:</strong> A 6-month delay to improve NOI by $30,000 can add $460,000+ to your property value at a 6.5% cap rate - often worth the wait.
              </p>
            </div>
          </details>
          
          <details className="bg-gradient-to-br from-slate-50 to-gray-50 rounded-xl border border-gray-200 overflow-hidden group">
            <summary className="flex items-center justify-between p-6 cursor-pointer">
              <h3 className="text-lg font-bold text-gray-900 pr-4">How far back do buyers look beyond T12?</h3>
              <i className="ri-arrow-down-s-line text-2xl text-teal-600 group-open:rotate-180 transition-transform"></i>
            </summary>
            <div className="px-6 pb-6 text-gray-700">
              <p className="mb-3">
                While T12 is the primary focus, sophisticated buyers typically review 3-5 years of historical financial data to identify trends, verify consistency, and assess the property's performance through different market cycles.
              </p>
              <p>
                They look for patterns in income growth, expense management, occupancy rates, and capital expenditures to validate the T12 figures and project future performance. Consistent positive trends over multiple years significantly strengthen buyer confidence.
              </p>
            </div>
          </details>
          
          <details className="bg-gradient-to-br from-slate-50 to-gray-50 rounded-xl border border-gray-200 overflow-hidden group">
            <summary className="flex items-center justify-between p-6 cursor-pointer">
              <h3 className="text-lg font-bold text-gray-900 pr-4">Do I need to include property management fees if I self-manage?</h3>
              <i className="ri-arrow-down-s-line text-2xl text-teal-600 group-open:rotate-180 transition-transform"></i>
            </summary>
            <div className="px-6 pb-6 text-gray-700">
              <p className="mb-3">
                Yes, you should include market-rate property management fees (typically 8-10% of collected income) in your T12 even if you self-manage. Here's why:
              </p>
              <ul className="space-y-2 text-sm mb-3">
                <li className="flex items-start gap-2">
                  <i className="ri-arrow-right-s-line text-teal-600 mt-0.5"></i>
                  <span>Buyers will add this expense when calculating NOI anyway</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-arrow-right-s-line text-teal-600 mt-0.5"></i>
                  <span>Most buyers won't self-manage and need accurate operating costs</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-arrow-right-s-line text-teal-600 mt-0.5"></i>
                  <span>Lenders require normalized expenses including management</span>
                </li>
              </ul>
              <p className="text-sm bg-yellow-50 border-l-4 border-yellow-500 rounded-r-lg p-4">
                <strong>Warning:</strong> If you don't include management fees, buyers will reduce your NOI by 8-10%, which can lower your property value by $400,000-$500,000 on a $3M property.
              </p>
            </div>
          </details>
        </div>
      </div>
    </section>
  );
};